// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { TeamsMeetingLinkLocator } from '@azure/communication-calling';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CallAdapter,
  CallAdapterState,
  CallAndChatLocator,
  CallParticipantsLocator,
  CallWithChatAdapter,
  CallWithChatAdapterState,
  TeamsCallAdapter
} from '@azure/communication-react';
import { Call, CallWithChat } from './views/Call';
import './App.css';
import { Landing } from './views/Landing';
import { Page } from './views/Page';
// import { useCalendarEvents } from 'hooks/useCalendarEvents';
import { constructPSTNCall, joinMeeting } from 'utils/joinMeeting';
import { callWithChatCompositeLogger } from 'error-telemetry/azureAdapterLogger';
import { NewAcsMeetingDetails } from 'utils/createNewACSMeeting';
import { locatorFromUrl } from 'utils/locatorFromUrl';
import { SwitchableThemeProvider, useBranding } from 'theme/SwitchableThemeProvider';
import { useIsMobile } from 'hooks/useIsMobile';
import { MobileInterstitial } from 'views/MobileInterstitial';
import { PreparingYourSession } from 'components/PreparingYourSession';
import { JoinCallInfo } from 'components/YourDay';
import { PSTNCallInfo } from 'components/PSTNCallModal';
import { StyledSpinner } from 'components/StyledSpinner';
import { ExperimentProvider, useExperiments } from 'experiments/ExperimentsProvider';
import { saveLatestCallIdToLocalStorage } from 'utils/callIdTracking';
import { useBotServerAppInsights, useFunctionsAppToken, useSignIn, useUserName } from 'hooks/useUser';
import { SignInProvider } from 'authentication/SignInProvider';
import { JoinCallIdentityKind } from 'components/JoinCallButton';
import { useMsal } from '@azure/msal-react';
import { SpeakToBot } from 'views/SpeakToBot';
import { triggerNewBotSession, SpeakToBotSessionManager } from 'utils/speakToBotUtils';
import { callWithEventTracking } from 'utils/BotServerAppInsights';
import { SpeakToBotErrorBoundary } from 'error-telemetry/SpeakToBotErrorBoundary';

declare const CALLING_SDK_VERSION: string;
declare const CHAT_SDK_VERSION: string;
declare const COMMUNICATION_REACT_SDK_VERSION: string;

console.log(`ACS self host app. Using @azure/communication-calling: ${CALLING_SDK_VERSION}`);
console.log(`@azure/communication-chat: ${CHAT_SDK_VERSION}.`);
console.log(`@azure/communication-react: ${COMMUNICATION_REACT_SDK_VERSION}.`);

type PageState =
  | 'CheckingIsSignedIn'
  | 'NotSignedIn'
  | 'Welcome'
  | 'SpeakToBot'
  | 'MobileInterstitial'
  | 'SettingUpCall'
  | 'CallInProgress';

type SupportedLocators = CallAndChatLocator | TeamsMeetingLinkLocator | CallParticipantsLocator;

type CallLocatorInfo = {
  locator: SupportedLocators;
  joinIdentity: JoinCallIdentityKind;
  callTitle?: string;
  callDetails?: string;
  alternateCallerId?: string;
};

function App(): JSX.Element {
  const { signedInState, refreshTokens } = useSignIn();
  const userName = useUserName();
  const botServerAppInsights = useBotServerAppInsights();
  const functionsAppToken = useFunctionsAppToken();
  // const [_, refreshCalendarEvents] = useCalendarEvents();
  const lastCallStateRef = useRef<string | undefined>(undefined);
  const [callLocatorInfo, setCallLocatorInfo] = useState<CallLocatorInfo | 'creating' | undefined>();
  const [speakToBotSessionManager, setSpeakToBotSessionManager] = useState<SpeakToBotSessionManager>();

  // Used for PSTN calls and calls with Teams identities
  const [callAdapter, setCallAdapter] = useState<CallAdapter | TeamsCallAdapter>();
  // Used for ACS Group Calls and Teams Meetings with ACS identities
  const [callWithChatAdapter, setCallWithChatAdapter] = useState<CallWithChatAdapter>();
  const [callId, setCallId] = useState<string>();

  const { experimentValues } = useExperiments();
  const playLobbyMusic = experimentValues.lobbyMusic.activeValue;

  // Check if the URL contains a joining meeting.
  const [callInterstitialDetails, setCallInterstitialDetails] =
    useState<{ locator: CallAndChatLocator | TeamsMeetingLinkLocator; joinIdentity: JoinCallIdentityKind }>();
  const isMobile = useIsMobile();
  useEffect(() => {
    const locator = locatorFromUrl();
    const joinIdentity = 'acs'; // TODO: when we support a persistant preference, update this to use the user's preference of acs vs teams identity
    if (locator) {
      if (isMobile) {
        setCallInterstitialDetails({ locator, joinIdentity });
      } else {
        setCallLocatorInfo({ locator, joinIdentity });
      }
    }
  }, []);

  const onCallIdUpdated = useCallback(
    (callId: string | undefined): void => {
      setCallId((prev) => {
        if (callId && callId !== prev) {
          saveLatestCallIdToLocalStorage(callId);
        }
        return callId;
      });
    },
    [setCallId, saveLatestCallIdToLocalStorage]
  );

  useEffect(() => {
    const onStateChange = (state: CallWithChatAdapterState): void => onCallIdUpdated(state.call?.id);
    callWithChatAdapter?.onStateChange(onStateChange);
    return () => {
      callWithChatAdapter?.offStateChange(onStateChange);
    };
  }, [callWithChatAdapter, onCallIdUpdated]);

  useEffect(() => {
    const onStateChange = (state: CallAdapterState): void => onCallIdUpdated(state.call?.id);
    callAdapter?.on('callEnded', resetToHome);
    callAdapter?.onStateChange(onStateChange);
    return () => {
      callAdapter?.offStateChange(onStateChange);
      callAdapter?.off('callEnded', resetToHome);
    };
  }, [callAdapter, onCallIdUpdated]);

  const branding = useBranding();
  const lobbyAudio = useRef<HTMLAudioElement>(new Audio());
  useEffect(() => {
    const wasPlaying = !lobbyAudio.current?.paused;
    lobbyAudio.current.src = branding.lobbySoundUrl;
    lobbyAudio.current.loop = true;
    lobbyAudio.current.volume = 0.15;
    lobbyAudio.current.load();
    if (wasPlaying) {
      lobbyAudio.current.play();
    }
  }, [branding.lobbySoundUrl]);

  // useEffect for dealing with lobbyMusic
  useEffect(() => {
    const lobbyMusicHandler = (): void => {
      const currentCallState = callWithChatAdapter?.getState().call?.state;

      if (currentCallState === 'InLobby' && lobbyAudio.current.paused) {
        lobbyAudio.current.play();
      } else if (currentCallState !== 'InLobby' && !lobbyAudio.current.paused) {
        lobbyAudio.current.pause();
        lobbyAudio.current.currentTime = 0;
      }

      if (currentCallState) {
        lastCallStateRef.current = currentCallState;
      }
    };
    if (playLobbyMusic) {
      callWithChatAdapter?.onStateChange(lobbyMusicHandler);

      const currentCallState = callWithChatAdapter?.getState().call?.state;
      if (currentCallState === 'InLobby') {
        lobbyAudio.current.play();
      }
    }
    return () => {
      lobbyAudio.current.pause();
      lobbyAudio.current.currentTime = 0;
      lastCallStateRef.current = undefined;
      if (callWithChatAdapter) {
        callWithChatAdapter.offStateChange(lobbyMusicHandler);
      }
    };
  }, [callWithChatAdapter, playLobbyMusic]);

  // Forcefully return home by reloading the page, use only when critical errors could occur
  const reloadHome = useCallback((): void => {
    window.location.href = location.origin + location.pathname;
  }, []);

  // Gracefully return home without having to reload the page
  const resetToHome = useCallback(async (): Promise<void> => {
    window.history.pushState({}, document.title, location.origin + location.pathname);

    callWithChatAdapter?.dispose();
    callAdapter?.dispose();
    setCallInterstitialDetails(undefined);
    setCallLocatorInfo(undefined);
    setCallAdapter(undefined);
    setCallWithChatAdapter(undefined);
    setCallId(undefined);

    if (speakToBotSessionManager) {
      botServerAppInsights?.trackEventStart('BotSession_End');
      try {
        await speakToBotSessionManager.disconnect();
        botServerAppInsights?.trackEventSuccess('BotSession_End');
      } catch (e) {
        botServerAppInsights?.trackEventFail('BotSession_End');
        throw e;
      } finally {
        botServerAppInsights?.finishSession();
      }
    }
    setSpeakToBotSessionManager(undefined);

    // lets refresh all of our tokens so we can take on any new actions when we are on the welcome screen
    refreshTokens();
    // refreshCalendarEvents();
  }, [
    refreshTokens,
    // refreshCalendarEvents,
    callWithChatAdapter,
    callAdapter,
    speakToBotSessionManager,
    botServerAppInsights
  ]);

  const { instance, accounts } = useMsal();
  const msalInstanceRef = useRef(instance);
  msalInstanceRef.current = instance;
  const msalAccountsRef = useRef(accounts);
  msalAccountsRef.current = accounts;

  useEffect(() => {
    (async () => {
      // Todo: make sure only one async IIFE is triggered at any given time
      if (
        userName &&
        functionsAppToken &&
        callLocatorInfo &&
        callLocatorInfo !== 'creating' &&
        !(callWithChatAdapter || callAdapter)
      ) {
        try {
          if (isPSTNCall(callLocatorInfo.locator)) {
            if (!callLocatorInfo.alternateCallerId) {
              throw new Error('Alternate caller id is required for PSTN calls');
            }
            const { callAdapter } = await constructPSTNCall(
              callLocatorInfo.locator,
              userName,
              functionsAppToken,
              callLocatorInfo.alternateCallerId,
              callLocatorInfo.joinIdentity,
              {
                instance: msalInstanceRef.current,
                accounts: msalAccountsRef.current
              }
            );
            setCallAdapter(callAdapter);
          } else {
            const { adapter } = await joinMeeting(
              callLocatorInfo.locator,
              userName,
              functionsAppToken,
              branding.videoBackgrounds,
              callLocatorInfo.joinIdentity,
              {
                instance: msalInstanceRef.current,
                accounts: msalAccountsRef.current
              }
            );

            const adapterIsCallWithChat = (
              adapter: CallWithChatAdapter | TeamsCallAdapter
            ): adapter is CallWithChatAdapter => 'sendMessage' in adapter;

            if (adapterIsCallWithChat(adapter)) {
              setCallWithChatAdapter(adapter);
            } else {
              setCallAdapter(adapter);
            }
          }
        } catch (e) {
          callWithChatCompositeLogger.error(`Error occurred when creating adapter: ${e}`);
          console.error(e);
          // Todo: create banner message
          alert('Error occurred when joining call');
          resetToHome();
        }
      }
    })();
  }, [userName, functionsAppToken, callLocatorInfo, callWithChatAdapter, callAdapter]);

  const pageState: PageState =
    signedInState === 'NotSignedIn'
      ? 'NotSignedIn'
      : signedInState === 'CheckingState'
      ? 'CheckingIsSignedIn'
      : speakToBotSessionManager
      ? 'SpeakToBot'
      : callWithChatAdapter || callAdapter
      ? 'CallInProgress'
      : callLocatorInfo
      ? 'SettingUpCall'
      : callInterstitialDetails
      ? 'MobileInterstitial'
      : 'Welcome';

  switch (pageState) {
    case 'CheckingIsSignedIn':
      return (
        <Page onHomeClick={reloadHome} callId={callId} verticalAlign="center">
          <StyledSpinner label="Fetching sign in state. Ensure popups are not blocked for this site." />
        </Page>
      );
    case 'Welcome':
    case 'NotSignedIn':
      return (
        <Page onHomeClick={resetToHome} callId={callId}>
          <Landing
            onStartACSCall={(meetingDetails: NewAcsMeetingDetails) => {
              setCallLocatorInfo('creating');
              const locator: CallAndChatLocator = {
                callLocator: {
                  groupId: meetingDetails.groupId
                },
                chatThreadId: meetingDetails.threadId
              };
              setCallLocatorInfo({ locator, joinIdentity: meetingDetails.joinIdentity });
            }}
            onJoinFromLinkClick={async (callInfo: JoinCallInfo) => {
              setCallLocatorInfo({
                locator: { meetingLink: callInfo.url },
                callTitle: callInfo.meetingName,
                callDetails: callInfo.meetingDetails,
                joinIdentity: callInfo.joinIdentity
              });
            }}
            onSpeakToBot={async () => {
              try {
                if (!functionsAppToken) {
                  throw new Error('Invalid app token');
                }

                botServerAppInsights?.newSession();

                const botSessionDataResponse = await callWithEventTracking(
                  async () => await triggerNewBotSession(functionsAppToken, botServerAppInsights),
                  botServerAppInsights,
                  'BotSession_TriggerNewBotSession'
                );

                const speakToBotSessionManager = await SpeakToBotSessionManager.create(
                  functionsAppToken,
                  botSessionDataResponse,
                  botServerAppInsights
                );

                setSpeakToBotSessionManager(speakToBotSessionManager);
              } catch (e) {
                callWithChatCompositeLogger.error(`Error occurred when starting ACS Copilot: ${e}`);
                console.error(e);
                alert('Error occurred when starting ACS Copilot');
              }
            }}
            onStartPSTNCallClick={async (callInfo: PSTNCallInfo) => {
              setCallLocatorInfo({
                locator: { participantIds: [callInfo.numberToCall] },
                alternateCallerId: callInfo.alternateCallerId,
                joinIdentity: callInfo.joinIdentity
              });
            }}
          />
        </Page>
      );
    case 'MobileInterstitial':
      return (
        <Page onHomeClick={resetToHome} callId={callId} verticalAlign="center">
          <MobileInterstitial
            deepLinkDetails={{
              locator: callInterstitialDetails?.locator,
              continueOnBrowser: () => {
                if (!callInterstitialDetails?.locator) {
                  throw new Error('Locator is required to continue on browser');
                }
                setCallLocatorInfo(callInterstitialDetails);
              }
            }}
          />
        </Page>
      );
    case 'SettingUpCall':
      return (
        <Page onHomeClick={resetToHome} callId={callId} verticalAlign="center">
          <PreparingYourSession
            callTitle={callLocatorInfo !== 'creating' ? callLocatorInfo?.callTitle : undefined}
            callDescription={callLocatorInfo !== 'creating' ? callLocatorInfo?.callDetails : undefined}
          />
        </Page>
      );
    case 'CallInProgress':
      return (
        <Page onHomeClick={resetToHome} callId={callId} horizontalFill={true}>
          {callWithChatAdapter && (
            <CallWithChat
              onHomeClick={resetToHome}
              adapter={callWithChatAdapter}
              locator={
                callLocatorInfo === 'creating' || !callLocatorInfo?.locator || isPSTNCall(callLocatorInfo?.locator)
                  ? undefined
                  : callLocatorInfo.locator
              }
              callTitle={callLocatorInfo !== 'creating' ? callLocatorInfo?.callTitle : undefined}
            />
          )}
          {callAdapter && <Call adapter={callAdapter} onHomeClick={resetToHome} />}
        </Page>
      );
    case 'SpeakToBot':
      return (
        <Page onHomeClick={resetToHome} horizontalFill={true}>
          <SpeakToBotErrorBoundary appInsights={botServerAppInsights}>
            {speakToBotSessionManager && <SpeakToBot sessionManager={speakToBotSessionManager} />}
          </SpeakToBotErrorBoundary>
        </Page>
      );
  }
}

const isPSTNCall = (locator: SupportedLocators): locator is CallParticipantsLocator => {
  return 'participantIds' in locator;
};

export default (): JSX.Element => {
  return (
    <ExperimentProvider>
      <SwitchableThemeProvider>
        <SignInProvider>
          <App />
        </SignInProvider>
      </SwitchableThemeProvider>
    </ExperimentProvider>
  );
};
